import { CONSOLE_FORMATS } from '../constants/CONSOLE_FORMATS';
/**
 * @group Services
 * 
 * @summary
 * <blockquote>Console logging service implementation</blockquote>
 * 
 * This service handles console-based event reporting with support for different
 * log levels and output formats. It's the default service when no specific
 * service is selected.
 * 
 * @example
 * ```typescript
 * import { ConsoleService } from '@shared/reporting';
 * 
 * const service = new ConsoleService(event);
 * await service.report();
 * ```
 */

import { tryCatch, ok, err, convertToAppError, AsyncResult, AppError, getEnvironment } from '@shared/utils';
import { HTTP_STATUS_NAMES } from '@shared/constants';
import { BaseEvent } from '../types/BaseEvent';
import { ReportingService } from '../interfaces/ReportingService';
import { getResource, ResourceConfig, ResourceReportingConfig } from '@shared/config';
import { EnvironmentReportingConfig } from '@shared/config/src/interfaces';
import { CONSOLE_FIELDS, CONSOLE_MESSAGES, CONSOLE_TABLE } from '../constants';

/**
 * @internal
 */
export class ConsoleService implements ReportingService {
  public event: BaseEvent;
  private config: ResourceReportingConfig | undefined;
  private currentEnvConfig: EnvironmentReportingConfig | undefined;
  private resourceConfig: ResourceConfig | null | undefined;
  private timestamp: string;
  private readonly initializationError: AppError | undefined;

  constructor(event: BaseEvent) {
    this.event = event;
    this.resourceConfig = getResource();
    this.config = this.resourceConfig?.reporting;
    this.timestamp = new Date(Date.now()).toISOString();
    
    // Get current environment and config
    const envResult = getEnvironment();
    if (envResult.isErr) {
      // Store error instead of throwing - will be returned in report()
      this.initializationError = envResult.getFail();
      return;
    }
    const currentEnv = envResult.getSuccess();
    
    this.currentEnvConfig = this.config?.[currentEnv];
  }

  /**
   * @summary
   * <blockquote>Build a formatted table for console logging</blockquote>
   * 
   * @param title - The table title
   * @param data - The data to display in the table
   * @returns Formatted table string
   * @private
   */
  private buildTable(title: string, data: Record<string, unknown>): string {
    const entries = Object.entries(data);
    if (entries.length === 0) return '';

    // Find the longest key for proper alignment
    const maxKeyLength = Math.max(...entries.map(([key]) => key.length));

    // Build the table
    let table = `\n${CONSOLE_TABLE.TITLE_PREFIX}${title}\n`;
    table += CONSOLE_TABLE.SEPARATOR_CHAR.repeat(maxKeyLength + 20) + '\n';

    entries.forEach(([key, value]) => {
      const formattedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      const padding = ' '.repeat(maxKeyLength - key.length);
      table += `${key}${padding}${CONSOLE_TABLE.COLUMN_SEPARATOR}${formattedValue}\n`;
    });

    return table;
  }

  /**
   * @summary
   * <blockquote>Build a nice console message for INFO level</blockquote>
   * 
   * @returns Formatted console message
   * @private
   */
  private buildInfoMessage(): string {
    // Required fields table
    const requiredFields = {
      [CONSOLE_FIELDS.EVENT_ID]: this.event.id,
      [CONSOLE_FIELDS.EVENT_NAME]: this.event.name,
      [CONSOLE_FIELDS.USER_ID]: this.event.distinctId,
      [CONSOLE_FIELDS.TIMESTAMP]: this.timestamp
    };

    let message = this.buildTable(CONSOLE_TABLE.EVENT_DETAILS_TITLE, requiredFields);

    // Props table (only if props exist and are not empty)
    if (this.event.props && Object.keys(this.event.props).length > 0) {
      message += this.buildTable(CONSOLE_TABLE.EVENT_PROPERTIES_TITLE, this.event.props);
    }

    return message;
  }

  /**
   * @summary
   * <blockquote>Build JSON message for JSON level</blockquote>
   * 
   * @returns JSON formatted console message
   * @private
   */
  private buildJsonMessage(): string {
    return JSON.stringify({
      event: {
        id: this.event.id,
        name: this.event.name,
        distinctId: this.event.distinctId,
        timestamp: this.timestamp,
        props: this.event.props
      }
    }, null, 2);
  }

  /**
   * @summary
   * <blockquote>Internal console logging logic</blockquote>
   * 
   * @private
   */
  private logToConsoleInternal(): void {
    const format = this.currentEnvConfig?.consoleFormat || CONSOLE_FORMATS.JSON;

    let logMessage: string;

    if (format === CONSOLE_FORMATS.JSON) {
      logMessage = this.buildJsonMessage();
    } else {
      logMessage = this.buildInfoMessage();
    }

    // For console transport, log directly to console
    // eslint-disable-next-line @template/console-log
    console.log(logMessage);
  }

  async report(): AsyncResult<void, AppError> {
    // Check for initialization error first
    if (this.initializationError) {
      return err(this.initializationError);
    }

    // If forceConsole is true, or if the service type is already console, proceed with logging
    const result = tryCatch(() => this.logToConsoleInternal());

    return result.match({
      ok: () => ok(undefined),
      err: (error: AppError) => err(convertToAppError(
        CONSOLE_MESSAGES.CONSOLE_LOGGING_FAILED,
        CONSOLE_MESSAGES.FAILED_TO_LOG_EVENT,
        HTTP_STATUS_NAMES.INTERNAL_SERVER_ERROR
      ))
    });
  }
}
